/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import {
  getPositiveAngle,
  convertToPolar,
  getCenterCoord,
  Sector
} from '#utils/PRIVATE_meterUtils';

import { CenterContext } from './meterCircle.types';

type Dimensions = {
  width: number;
  height: number;
  innerRadius: number;
  outerRadius: number;
};

/**
 * Returns the true dimensions of the meter circle after initial render. All dimensions are 0 for initial render.
 * @param element The HTML element that wraps the track and indicator div.
 * @param section The section in which the meter circle lies.
 * @returns The dimensions of the meter circle.
 */
export function getDimensions(element: HTMLElement | null, section: Sector): Dimensions {
  if (!element) {
    return {
      width: 0,
      height: 0,
      innerRadius: 0,
      outerRadius: 0
    };
  }

  const circleTrack = element.children[0] as HTMLElement;
  const rect = circleTrack.getBoundingClientRect();

  return {
    width: Math.round(rect.width),
    height: Math.round(rect.height),
    ...getInnerAndOuterRadius(circleTrack, rect, section)
  };
}

/**
 * Returns the inner and outer radius of the meter circle.
 * @param element The HTML element that wraps the track and indicator div.
 * @param rect The bounding rect of the wrapper HTML element.
 * @param section The section in which the meter circle lies.
 * @returns The inner and outer radius of the meter circle.
 */
function getInnerAndOuterRadius(
  element: HTMLElement,
  rect: { width: number; height: number },
  section: string
): {
  innerRadius: number;
  outerRadius: number;
} {
  const width = element.clientWidth;
  const height = element.clientHeight;

  if (section === 'bottom' || section === 'top') {
    return { outerRadius: rect.width / 2, innerRadius: height };
  } else if (section === 'left' || section === 'right') {
    return { outerRadius: rect.height / 2, innerRadius: width };
  }
  return { innerRadius: width / 2, outerRadius: rect.width / 2 };
}

/**
 * Returns the value corresponding to clicked point in the meter circle. Returns undefined if the clicked point is
 * outside the track of the meter circle.
 * @param pageX The pageX of the click event
 * @param pageY The pageY of the click event
 * @param max The max value of the meter circle.
 * @param min The min value of the meter circle.
 * @param step The step of the metercircle.
 * @param dimensionsRef The ref that stores dimensions reference of meter circle.
 * @param startAngle The startAngle of the meter circle.
 * @param angleExtent The angleExtent of the meter circle
 * @returns {number} The value corresponding to clicked point in the meter circle.
 */
export function getValue(
  event: PointerEvent,
  min: number,
  max: number,
  step: number,
  dimensionsRef: RefObject<Dimensions>,
  startAngle: number,
  angleExtent: number,
  section: Sector,
  isRtl: boolean
): number | undefined {
  const dims = dimensionsRef.current;
  if (!dims) {
    return undefined;
  }

  const center = getCenterCoord(section);
  const { angle } = convertToPolar(
    dims.width * center.x,
    dims.height * center.y,
    event.offsetX,
    event.offsetY
  );

  const positiveAngle = getPositiveAngle(angle);

  let val;
  if (isRtl && startAngle + angleExtent > positiveAngle) {
    val = (getPositiveAngle(angle - startAngle) / angleExtent) * (max - min);
  }

  if (!isRtl) {
    const clockwiseArc = getPositiveAngle(startAngle - positiveAngle);
    if (clockwiseArc <= angleExtent) {
      val = (clockwiseArc * (max - min)) / angleExtent;
    }
  }

  if (val != undefined) {
    val = Math.round(val / step) * step;
    val = Math.max(Math.min(max, val), min);
  }
  return val;
}

/**
 * Returns the fraction of angle extent for given delta.
 * @param delta The delta of metric values.
 * @param min The minimum value of the meter circle.
 * @param max The maximum value of the meter circle.
 * @param angleExtent The angle extent of the meter circle.
 * @returns The angle extent corresponding to delta in metric value.
 */
export function getAngleExtentFromDelta(
  delta: number,
  min: number,
  max: number,
  angleExtent: number
): number {
  return (delta * angleExtent) / (max - min);
}

/**
 * Returns the start angle for given metric value.
 * @param value The metric value
 * @param min The minimum value of the meter circle.
 * @param max The maximum value of the meter circle.
 * @param startAngle The startAngle of the meter circle.
 * @param angleExtent The angleExtent of the meter circle.
 * @param isRtl Whether the reading mode is 'rtl'
 * @returns The startAngle for the metric value.
 */
export function getStartAngleForValue(
  value: number,
  min: number,
  max: number,
  startAngle: number,
  angleExtent: number,
  isRtl: boolean
): number {
  const extent = getAngleExtentFromDelta(value - min, min, max, angleExtent);
  return startAngle + (isRtl ? 1 : -1) * extent;
}

/**
 * Returns the center context of for the meter circle.
 */
export function getCenterContext(
  dimensionsRef: RefObject<Dimensions>,
  section: 'top' | 'bottom' | 'left' | 'right' | 'full'
): CenterContext | undefined {
  const dims = dimensionsRef.current;

  if (!dims) {
    return;
  }

  const center = getCenterCoord(section);
  const cx = center.x * dims.width;
  const cy = center.y * dims.height;

  const isHoriz = section === 'top' || section === 'bottom';

  let innerX, innerY, innerW, innerH;
  let outerX, outerY, outerW, outerH;

  if (isHoriz || section === 'full') {
    outerW = 2 * dims.innerRadius;
    outerH = section === 'full' ? 2 * dims.innerRadius : dims.innerRadius;
    outerX = cx - dims.innerRadius;
    outerY = section === 'bottom' ? 0 : cy - dims.innerRadius;

    innerW = Math.sqrt(2) * dims.innerRadius;
    innerH = section === 'full' ? innerW : dims.innerRadius / Math.sqrt(2);
    innerX = cx - dims.innerRadius / Math.sqrt(2);
    innerY = section === 'bottom' ? 0 : cy - dims.innerRadius / Math.sqrt(2);
  } else {
    outerH = 2 * dims.innerRadius;
    outerW = dims.innerRadius;
    outerX = section === 'left' ? cx - dims.innerRadius : 0;
    outerY = cy - dims.innerRadius;

    innerH = Math.sqrt(2) * dims.innerRadius;
    innerW = dims.innerRadius / Math.sqrt(2);
    innerX = section === 'left' ? cx - dims.innerRadius / Math.sqrt(2) : 0;
    innerY = cy - dims.innerRadius / Math.sqrt(2);
  }

  return {
    outerBounds: {
      x: outerX,
      y: outerY,
      width: outerW,
      height: outerH
    },
    innerBounds: {
      x: innerX,
      y: innerY,
      width: innerW,
      height: innerH
    }
  };
}
